import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon, BuildingOfficeIcon, CubeIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { PageLoader, Button } from "../../ui";
import {
  FormModal,
  FormModalHeader,
  FormModalBody,
  FormModalField,
  FormModalFooter,
  formInputCls,
} from "../../ui/FormModal";
import VaptStatusBadge from "../components/VaptStatusBadge";
import VaptAssetInventoryModal from "../components/VaptAssetInventoryModal";
import {
  listVaptProjectsApi,
  createVaptProjectApi,
  listVaptEnabledCustomersApi,
  listVaptExecutionCoverageApi,
  getVaptCustomerEntitlementApi,
  createVaptScopeApi,
  createVaptTargetApi,
  createVaptAuthorizationApi,
  approveVaptAuthorizationApi,
  syncVaptProjectFromAssetManagerApi,
  getVaptAssetManagerAssetsApi,
} from "../../../services/axios/AuthApis/VAPTApis";
import { useAuth } from "../../../context/AuthContext";
import { VAPT_DOMAINS } from "../utils/vaptConstants";
import { validateTargetForDomain } from "../utils/targetValidation";
import { getVaptErrorMessage } from "../utils/apiError";

const PLATFORM_ADMIN_ROLES = ["platform_admin", "admin"];
// Mirrors AuthorizationViewSet._AUTH_DECISION_ROLES on the backend.
const AUTH_APPROVER_ROLES = ["platform_admin", "admin", "customer_admin"];

const NO_ACCESS = { vaptEnabled: false, assetVaptEnabled: false, externalVaptEnabled: false };
// Platform Admin's unrestricted "All Customers (Global)" view — not bound to
// any single customer's entitlement.
const ADMIN_GLOBAL_ACCESS = { vaptEnabled: true, assetVaptEnabled: true, externalVaptEnabled: true };

// This form creates ONLY External VAPT projects — Asset VAPT projects are
// never manually created, they originate from AI Asset Manager's real
// synced asset inventory (see the auto-sync effect below, which calls
// syncVaptProjectFromAssetManagerApi). Field set is scoped strictly to an
// external engagement per the access-control spec: no asset picker, no
// VAPT-type selector.
const emptyForm = {
  name: "",
  description: "",
  domain: "",
  in_scope: [],
  out_of_scope: [],
  authorized: false,
  start_date: "",
  end_date: "",
  environment: "Production",
};

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);


// Small tag/chip input: type + Enter/comma to add, click × to remove.
const TagInput = ({ values, onChange, placeholder, error }) => {
  const [draft, setDraft] = useState("");

  const commit = () => {
    const v = draft.trim().replace(/,$/, "");
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft("");
  };

  return (
    <div>
      <div className={`flex flex-wrap gap-1.5 p-2 border rounded-lg bg-gray-50 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-500 ${error ? "border-red-300" : "border-gray-200"}`}>
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
            {v}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="text-primary-400 hover:text-primary-700">
              ×
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => {
            if (e.target.value.endsWith(",")) {
              const v = e.target.value.slice(0, -1).trim();
              if (v && !values.includes(v)) onChange([...values, v]);
              setDraft("");
            } else {
              setDraft(e.target.value);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Backspace" && !draft && values.length > 0) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={commit}
          placeholder={values.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[140px] text-sm bg-transparent outline-none py-0.5"
        />
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};

const VaptProjects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformAdmin = PLATFORM_ADMIN_ROLES.includes(user?.role);
  const canApproveAuthorization = AUTH_APPROVER_ROLES.includes(user?.role);

  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [coverageByProject, setCoverageByProject] = useState({});
  const [access, setAccess] = useState(NO_ACCESS);
  // Tracks whether the Asset Manager auto-sync has run for the currently
  // viewed customer, and its outcome — 'idle' (not run yet), 'syncing',
  // 'no_assets' (ran, Asset Manager genuinely has zero synced assets for
  // this customer), 'error', or 'done' (created a project or one already
  // existed, nothing further to do).
  const [assetSyncStatus, setAssetSyncStatus] = useState("idle");
  // Asset VAPT is now rendered ONE CARD PER VAPT-ENABLED CUSTOMER, built
  // directly from the live, non-persisting Asset Manager endpoint
  // (getVaptAssetManagerAssetsApi) — not from iterating VAPTProject rows,
  // which could contain duplicates from repeated syncs. `assetVaptCustomers`
  // holds the list of {id, name} customers to render a card for;
  // `assetCardSnapshots` holds each customer's live asset summary keyed by
  // customer id.
  const [assetVaptCustomers, setAssetVaptCustomers] = useState([]);
  const [assetCardSnapshots, setAssetCardSnapshots] = useState({});
  const [inventoryModalCustomer, setInventoryModalCustomer] = useState(null);
  const [projectDetailsPendingId, setProjectDetailsPendingId] = useState(null);

  // The viewer's own customer id, resolved from the authenticated session
  // (useAuth) the same way other modules do — see e.g.
  // AssetManagement/index.js, AI_DSPM/tabs/Assets.js.
  const ownCustomerId = user?.customer_id || user?.id;

  // Platform Admin only, and sourced from the REAL VAPT entitlement roster
  // (/api/vapt/customers/ - id + name derived directly from
  // ai_crm.CustomerRequirement -> authentication.UserMaster, the same
  // source vapt/permissions.py's entitlement check itself reads), NOT
  // listVaptCustomersApi's CRM "leads" pipeline list. Leads are sales
  // prospects with their own unrelated document id/customer_id (a
  // "LEAD-..." string) - selecting one and sending its id as customer_id
  // would never match any real CustomerRequirement row, which is exactly
  // what caused "AI VAPT access is not enabled for this customer" to show
  // even when the CRM Additional Module toggles were genuinely ON. A
  // customer_admin doesn't need this roster at all - their own entitlement
  // lookup below always uses their own ownCustomerId directly.
  useEffect(() => {
    if (!isPlatformAdmin) return;
    listVaptEnabledCustomersApi()
      .then((list) => setCustomers(list || []))
      .catch(() => setCustomers([]));
    // eslint-disable-next-line
  }, [isPlatformAdmin]);

  useEffect(() => {
    listVaptExecutionCoverageApi()
      .then((cov) => setCoverageByProject(cov || {}))
      .catch(() => setCoverageByProject({}));
  }, []);

  // Real entitlement lookup — reuses CRM's existing customer-requirements
  // API via VAPTApis.getVaptCustomerEntitlementApi (see that function's
  // comment for the exact CRM call it wraps). Platform Admin viewing "All
  // Customers (Global)" gets the unrestricted admin view instead of a
  // single customer's entitlement.
  useEffect(() => {
    let mounted = true;
    if (isPlatformAdmin && !selectedCustomerId) {
      setAccess(ADMIN_GLOBAL_ACCESS);
      return;
    }
    const targetCustomerId = isPlatformAdmin ? selectedCustomerId : ownCustomerId;
    if (!targetCustomerId) {
      setAccess(NO_ACCESS);
      return;
    }
    // Platform Admin: the enabled-customers roster already carries each
    // customer's entitlement flags, so no extra per-customer CRM lookup
    // (which 404s for customers whose account no longer exists).
    if (isPlatformAdmin) {
      const entry = customers.find((c) => (c.id || c.customer_id) === targetCustomerId);
      if (entry) {
        setAccess({
          vaptEnabled: true,
          assetVaptEnabled: !!entry.asset_vapt_enabled,
          externalVaptEnabled: !!entry.external_vapt_enabled,
        });
      } else {
        setAccess(NO_ACCESS);
      }
      return;
    }
    getVaptCustomerEntitlementApi(targetCustomerId)
      .then((ent) => {
        if (mounted) setAccess(ent);
      })
      .catch(() => {
        if (mounted) setAccess(NO_ACCESS);
      });
    return () => {
      mounted = false;
    };
  }, [isPlatformAdmin, selectedCustomerId, ownCustomerId, customers]);

  const viewerCustomer = isPlatformAdmin
    ? customers.find((c) => (c.id || c.customer_id) === selectedCustomerId)
    : customers.find((c) => (c.id || c.customer_id) === ownCustomerId);

  const { vaptEnabled, assetVaptEnabled, externalVaptEnabled } = access;

  // Security rule: this must gate the ACTION, not just the button's
  // visibility — see openCreateModal/submit below, which re-check this
  // before ever opening/submitting the form.
  const [modalError, setModalError] = useState(null);
  // Why the External project action is unavailable (null = available).
  // Platform Admin still needs a specific customer selected, and that
  // customer must have External VAPT enabled. The backend re-checks both.
  let externalCreateBlockReason = null;
  if (isPlatformAdmin && !selectedCustomerId) {
    externalCreateBlockReason = "Select a specific customer to create an External VAPT project.";
  } else if (!externalVaptEnabled) {
    externalCreateBlockReason = "External VAPT is not enabled for this customer.";
  }
  const canCreateExternalProject = !externalCreateBlockReason;

  const load = () => {
    setLoading(true);
    setError(null);
    const filters = {};
    if (isPlatformAdmin) {
      if (selectedCustomerId) filters.customer_id = selectedCustomerId;
      // engagement_type is intentionally NOT filtered server-side here —
      // both project types are fetched, then split into sections below so
      // Platform Admin can see Asset + External side by side.
    } else {
      // Non-admin customers only ever see their own customer's projects —
      // the backend also enforces this via customer isolation regardless of
      // this filter, but sending it keeps the list scoped from the start.
      filters.customer_id = ownCustomerId;
    }
    listVaptProjectsApi(filters)
      .then((data) => {
        setAllProjects(asArray(data));
        setLoading(false);
      })
      .catch((err) => {
        setError(getVaptErrorMessage(err, "Failed to load projects."));
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [selectedCustomerId, ownCustomerId]);

  // Entitlement-filtered view: never show a project type the current
  // customer (or Platform Admin's selected customer) doesn't have access
  // to, even if the underlying mock data contains it.
  const visibleProjects = useMemo(() => {
    return allProjects.filter((p) => {
      if (p.engagement_type === "asset") return assetVaptEnabled;
      if (p.engagement_type === "external") return externalVaptEnabled;
      return false;
    });
  }, [allProjects, assetVaptEnabled, externalVaptEnabled]);

  const assetProjects = visibleProjects.filter((p) => p.engagement_type === "asset");
  const externalProjects = visibleProjects.filter((p) => p.engagement_type === "external");

  // Auto-trigger the real Asset Manager sync: if this customer has
  // assetVaptEnabled but zero Asset VAPT projects yet, ask the backend to
  // fetch their real synced assets (ai_cspm CloudAsset) and create the
  // project from them, instead of requiring a manual step that nothing in
  // this UI otherwise exposes. Runs once per (customer, project-list)
  // state — re-runs if the customer switch changes since assetSyncStatus
  // is reset by the effect below on customer change.
  const syncTargetCustomerId = isPlatformAdmin ? selectedCustomerId : ownCustomerId;

  useEffect(() => {
    setAssetSyncStatus("idle");
    // eslint-disable-next-line
  }, [syncTargetCustomerId]);

  useEffect(() => {
    if (!assetVaptEnabled || !syncTargetCustomerId || loading) return;
    if (assetProjects.length > 0) return;
    if (assetSyncStatus !== "idle") return;

    setAssetSyncStatus("syncing");
    syncVaptProjectFromAssetManagerApi(syncTargetCustomerId)
      .then((res) => {
        if (res?.created) {
          setAssetSyncStatus("done");
          load();
        } else {
          setAssetSyncStatus("no_assets");
        }
      })
      .catch(() => {
        setAssetSyncStatus("error");
      });
    // eslint-disable-next-line
  }, [assetVaptEnabled, syncTargetCustomerId, loading, assetProjects.length, assetSyncStatus]);

  // Resolve the set of customers to render an Asset VAPT card for:
  //  - Platform Admin, "All Customers (Global)": every VAPT-enabled customer
  //    (listVaptEnabledCustomersApi) whose real entitlement has
  //    assetVaptEnabled true.
  //  - Platform Admin with one customer selected, or a Customer Admin: just
  //    that single customer, if assetVaptEnabled.
  useEffect(() => {
    let mounted = true;
    if (isPlatformAdmin && !selectedCustomerId) {
      // Reuse the already-loaded roster (it carries entitlement flags) —
      // no extra request per customer. Orphaned customers (no account) have
      // no asset inventory to show and are skipped.
      setAssetVaptCustomers(
        customers
          .filter((c) => c.asset_vapt_enabled && c.has_account !== false)
          .map((c) => ({ id: c.id || c.customer_id, name: c.name || c.company_name || c.email || "Unnamed Customer" }))
      );
    } else if (assetVaptEnabled && syncTargetCustomerId) {
      setAssetVaptCustomers([
        {
          id: syncTargetCustomerId,
          name:
            viewerCustomer?.name ||
            viewerCustomer?.company_name ||
            (!isPlatformAdmin && (user?.company_name || user?.name)) ||
            "Asset VAPT Project",
        },
      ]);
    } else {
      setAssetVaptCustomers([]);
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line
  }, [isPlatformAdmin, selectedCustomerId, assetVaptEnabled, syncTargetCustomerId, customers]);

  // For each customer to display, fetch its live Asset Manager summary
  // (Total/Eligible/Unsupported etc.) — the primary data source for the
  // Asset VAPT card's numbers, independent of how many (if any)
  // engagement_type="asset" VAPTProject rows exist for that customer.
  useEffect(() => {
    let mounted = true;
    // Fetched in small serialized batches, not all at once: for Platform
    // Admin's "All Customers" view this can be a dozen+ customers, and each
    // call does a real, non-trivial Asset Manager/Mongo query. Firing them
    // all in parallel (Promise per customer, no limit) overwhelmed the dev
    // server / reverse proxy under concurrent load and produced real 504s
    // on several of them - reproduced live, not hypothetical.
    const CONCURRENCY = 3;
    const queue = [...assetVaptCustomers];
    const worker = async () => {
      while (queue.length > 0 && mounted) {
        const c = queue.shift();
        try {
          const res = await getVaptAssetManagerAssetsApi(c.id);
          if (mounted) setAssetCardSnapshots((prev) => ({ ...prev, [c.id]: res }));
        } catch (err) {
          if (mounted) setAssetCardSnapshots((prev) => ({ ...prev, [c.id]: null }));
        }
      }
    };
    Array.from({ length: Math.min(CONCURRENCY, assetVaptCustomers.length) }, worker);
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line
  }, [assetVaptCustomers]);

  // "View Project Details" — navigate to the existing persisted
  // engagement_type="asset" VAPTProject for this customer if one already
  // exists; otherwise trigger the persisting sync first (so the detail page
  // has something real to show) then navigate, or surface a clear message
  // if Asset Manager genuinely has no synced assets for this customer yet.
  const handleViewAssetProjectDetails = async (customerId) => {
    const existing = assetProjects.find((p) => String(p.customer_id) === String(customerId));
    if (existing) {
      navigate(`/ai-vapt/projects/${existing.project_id}`);
      return;
    }
    setProjectDetailsPendingId(customerId);
    setError(null);
    try {
      const res = await syncVaptProjectFromAssetManagerApi(customerId);
      if (res?.created) {
        const list = await listVaptProjectsApi({ customer_id: customerId, engagement_type: "asset" });
        const proj = asArray(list)[0];
        if (proj) {
          navigate(`/ai-vapt/projects/${proj.project_id}`);
          return;
        }
      }
      setError("No Asset VAPT engagement exists for this customer — no eligible assets are available in the asset inventory to create one from.");
    } catch (err) {
      setError(getVaptErrorMessage(err, "Failed to open project details."));
    } finally {
      setProjectDetailsPendingId(null);
    }
  };

  const openCreateModal = () => {
    if (externalCreateBlockReason) {
      setError(externalCreateBlockReason);
      return;
    }
    setError(null);
    setModalError(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  // The real VAPTProject.project_type field is a fixed choice list, not a
  // free-form "<domain> VAPT" string — map the form's target-domain
  // selection onto the closest real choice.
  const PROJECT_TYPE_BY_DOMAIN = {
    Web: "Web App Security Test",
    API: "API Security Test",
    Mobile: "Mobile App Security Test",
    Infrastructure: "Network Security Test",
    Cloud: "Cloud Security Assessment",
  };

  // Returns a user-facing error string, or null if the form is valid.
  // Mirrors the backend's own rules (at least one in-scope target,
  // authorization required, end after start) so the user sees the problem
  // immediately instead of round-tripping to a 400.
  const validateForm = () => {
    if (!form.name.trim()) return "Project Name is required.";
    if (!form.description.trim()) return "Description is required.";
    if (!form.domain) return "VAPT Domain is required.";
    if (form.in_scope.length === 0) return "At least one In-Scope Target is required.";
    if (!form.authorized) return "You must confirm authorization to perform security testing.";
    if (!form.start_date || !form.end_date) return "Testing window start and end dates are required.";
    if (new Date(form.end_date) <= new Date(form.start_date)) return "End date must be after start date.";
    const overlap = form.in_scope.find((t) => form.out_of_scope.includes(t));
    if (overlap) return `"${overlap}" cannot be both in-scope and out-of-scope.`;
    for (const t of [...form.in_scope, ...form.out_of_scope]) {
      const err = validateTargetForDomain(t, form.domain);
      if (err) return err;
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    // Errors raised while the modal is open are shown INSIDE the modal
    // (modalError) — the page-level banner sits behind the backdrop.
    if (externalCreateBlockReason) {
      setModalError(externalCreateBlockReason);
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setModalError(validationError);
      return;
    }
    setSaving(true);
    setModalError(null);
    let projectCreated = false;
    try {
      const targetCustomerId = isPlatformAdmin ? selectedCustomerId || undefined : ownCustomerId;

      // 1) Create the VAPTProject itself — only real VAPTProject fields go
      // in this payload (see vapt/models.py). engagement_type is fixed to
      // "external"; this form never creates Asset VAPT projects.
      const project = await createVaptProjectApi({
        name: form.name,
        description: form.description,
        engagement_type: "external",
        project_type: PROJECT_TYPE_BY_DOMAIN[form.domain] || "Penetration Testing",
        start_date: form.start_date,
        end_date: form.end_date,
        customer_id: targetCustomerId,
        client_name: viewerCustomer?.name,
      });

      // 2) Attach scope + one VAPTTarget per in-scope/out-of-scope entry +
      // (if attested) a project-wide authorization, as separate real
      // records referencing the new project's Mongo id — VAPTProject
      // itself has no target/in_scope/authorized fields.
      const projectRef = project?.id;
      projectCreated = !!projectRef;
      if (projectRef) {
        await createVaptScopeApi({
          project: projectRef,
          in_scope: form.in_scope,
          out_of_scope: form.out_of_scope,
          environment: form.environment,
        });
        await Promise.all([
          ...form.in_scope.map((value) =>
            createVaptTargetApi({
              project: projectRef,
              source: "external",
              external_value: value,
              target_type: form.domain,
              domain: form.domain,
              // Never created pre-authorized. A project-wide authorization
              // (below), once Approved through the dedicated endpoint,
              // transitions these targets to "Authorized" server-side.
              status: "Pending Authorization",
            })
          ),
          ...form.out_of_scope.map((value) =>
            createVaptTargetApi({
              project: projectRef,
              source: "external",
              external_value: value,
              target_type: form.domain,
              domain: form.domain,
              status: "Out of Scope",
            })
          ),
        ]);
        if (form.in_scope.length > 0) {
          // Project-wide authorization (target=None) - covers every
          // in-scope target of this project, matching _launch_scan's
          // fallback check on the backend. Always created "Pending" (the
          // backend ignores any status sent on create). If the creator
          // both attested authorization AND can approve, approve it now in
          // the same flow; the backend approve endpoint then transitions
          // every pending in-scope target to "Authorized".
          const auth = await createVaptAuthorizationApi({
            project: projectRef,
            evidence_reference: "Attested via VAPT project creation form",
          });
          if (form.authorized && canApproveAuthorization && auth?.authorization_id) {
            await approveVaptAuthorizationApi(auth.authorization_id);
          }
        }
      }

      setShowModal(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      const message = getVaptErrorMessage(err, "Failed to create project.");
      if (projectCreated) {
        // The project exists but a later setup step failed. Nothing is
        // deleted automatically; refresh the list so the user sees it and
        // can finish the setup under Scope & Targets.
        setModalError(`The project was created, but setup did not finish: ${message} Complete the scope and targets from the project's Scope & Targets tab.`);
        load();
      } else {
        setModalError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const renderProjectCard = (project) => {
    const coverage = coverageByProject[project.project_id];
    const isAssetProject = project.engagement_type === "asset";
    return (
      <div
        key={project.project_id}
        onClick={() => navigate(`/ai-vapt/projects/${project.project_id}`)}
        className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer"
      >
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-base font-bold text-gray-900 truncate">{project.name}</h3>
              <VaptStatusBadge status={project.status} size="sm" />
            </div>
            <p className="text-sm text-gray-500">{project.client_name || "—"}</p>
            {isAssetProject && (
              <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                <CubeIcon className="w-3.5 h-3.5" /> Source: AI Asset Manager
              </p>
            )}
            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{project.description || "—"}</p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {project.engagement_type && (
                <span className="text-xs font-semibold text-gray-600">
                  VAPT Type: {isAssetProject ? "Asset VAPT" : "External VAPT"}
                </span>
              )}
              {project.project_type && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  {project.project_type}
                </span>
              )}
            </div>
          </div>

          {isAssetProject ? (
            <div className="grid grid-cols-3 gap-4 text-center flex-shrink-0">
              <div>
                <p className="text-sm font-semibold text-gray-800">{coverage?.total_assets ?? "—"}</p>
                <p className="text-[10px] text-gray-400 uppercase">Assets</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-green-700">{coverage?.applicable ?? "—"}</p>
                <p className="text-[10px] text-gray-400 uppercase">Applicable</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-600">{coverage?.unsupported ?? "—"}</p>
                <p className="text-[10px] text-gray-400 uppercase">Unsupported</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 text-center flex-shrink-0">
              <div>
                <p className="text-xs font-semibold text-gray-800">{project.start_date || "—"}</p>
                <p className="text-[10px] text-gray-400 uppercase">Start</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800">{project.end_date || "—"}</p>
                <p className="text-[10px] text-gray-400 uppercase">End</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) return <PageLoader title="Loading projects…" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">VAPT Projects</h2>
          <p className="text-sm text-gray-500">Manage vulnerability assessment and penetration testing projects across assets and authorized external targets.</p>
        </div>
        {vaptEnabled && (
          <div className="flex flex-col items-end gap-1">
            <Button icon={PlusIcon} onClick={openCreateModal} disabled={!canCreateExternalProject} title={externalCreateBlockReason || undefined}>
              New External VAPT Project
            </Button>
            {externalCreateBlockReason && <p className="text-xs text-gray-500 max-w-sm text-right">{externalCreateBlockReason}</p>}
          </div>
        )}
      </div>

      {isPlatformAdmin && (
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-gray-500 uppercase">Customer</label>
            <select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm min-w-[220px]"
            >
              <option value="">All Customers (Global)</option>
              {customers.map((c) => (
                <option key={c.id || c.customer_id} value={c.id || c.customer_id}>
                  {c.name || c.company_name || c.email || "Unnamed Customer"}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5">{error}</div>
      )}

      {/* Case: no VAPT access at all (or a Platform Admin has selected a
          customer with VAPT fully disabled). */}
      {!vaptEnabled ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <LockClosedIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">AI VAPT access is not enabled for this customer.</p>
          <p className="text-sm text-gray-500 mt-1">Contact your account team to enable Asset VAPT or External VAPT.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {assetVaptEnabled && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-bold text-gray-800">Asset VAPT Projects</h3>
                <p className="text-xs text-gray-500">
                  VAPT projects are automatically created for eligible customers and synchronized with the latest Asset Manager inventory.
                </p>
              </div>
              <div className="grid gap-4">
                {assetVaptCustomers.map((c) => {
                  const snapshot = assetCardSnapshots[c.id];
                  const project = assetProjects.find((p) => String(p.customer_id) === String(c.id));
                  const status = project?.status || "Planning";
                  return (
                    <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-5">
                      <div className="flex items-start justify-between flex-wrap gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <h3 className="text-base font-bold text-gray-900 truncate">{c.name}</h3>
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-semibold">
                              Asset VAPT
                            </span>
                            <VaptStatusBadge status={status} size="sm" />
                          </div>
                          <p className="text-sm text-gray-500">Asset VAPT Project</p>
                          <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                            <CubeIcon className="w-3.5 h-3.5" /> Source: AI Asset Manager
                          </p>
                        </div>

                        <div className="grid grid-cols-3 gap-4 text-center flex-shrink-0">
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{snapshot?.summary?.total ?? "—"}</p>
                            <p className="text-[10px] text-gray-400 uppercase">Assets</p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-green-700">{snapshot?.summary?.eligible ?? "—"}</p>
                            <p className="text-[10px] text-gray-400 uppercase">Applicable</p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-amber-600">{snapshot?.summary?.unsupported ?? "—"}</p>
                            <p className="text-[10px] text-gray-400 uppercase">Unsupported</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                        <Button variant="secondary" onClick={() => setInventoryModalCustomer(c)}>
                          View Asset Inventory
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => handleViewAssetProjectDetails(c.id)}
                          disabled={projectDetailsPendingId === c.id}
                        >
                          {projectDetailsPendingId === c.id ? "Opening…" : "View Project Details"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {assetVaptCustomers.length === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <BuildingOfficeIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-700 font-medium">No Asset VAPT projects available</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Asset VAPT projects will appear here when a customer has Asset VAPT enabled and assets are available in Asset Manager.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {externalVaptEnabled && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-bold text-gray-800">External VAPT Projects</h3>
                <p className="text-xs text-gray-500">
                  Assess authorized external targets such as domains, IP addresses, APIs, and applications.
                </p>
              </div>
              <div className="grid gap-4">
                {externalProjects.map(renderProjectCard)}
                {externalProjects.length === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <BuildingOfficeIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No External VAPT projects yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* This modal only ever creates an External VAPT project — no Asset
          Manager selection, no VAPT-type picker, per the access-control spec. */}
      <FormModal open={showModal} onClose={() => setShowModal(false)} maxWidth="lg">
        <FormModalHeader
          icon={BuildingOfficeIcon}
          title="Create External VAPT Project"
          subtitle={
            viewerCustomer?.name
              ? `Customer: ${viewerCustomer.name} — targets, scope, and authorization required`
              : "Manually-defined external engagement — targets, scope, and authorization required"
          }
          onClose={() => setShowModal(false)}
        />
        <FormModalBody id="vapt-project-form" onSubmit={submit}>
          {modalError && (
            <div role="alert" className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5">
              {modalError}
            </div>
          )}
          <FormModalField label="Project Name" required>
            <input
              className={formInputCls(false)}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </FormModalField>
          {/* The backend requires a non-blank description (VAPTProject.description). */}
          <FormModalField label="Description" required>
            <textarea
              className={formInputCls(false)}
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
            />
          </FormModalField>
          <FormModalField label="VAPT Domain" required>
            <select
              className={formInputCls(false)}
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {VAPT_DOMAINS.map((d) => (
                <option key={d} value={d}>
                  {d === "Kubernetes" ? "Kubernetes / Containers" : d}
                </option>
              ))}
            </select>
          </FormModalField>
          <FormModalField label="In-Scope Targets" required>
            <TagInput
              values={form.in_scope}
              onChange={(v) => setForm({ ...form, in_scope: v })}
              placeholder="Type a target and press Enter — e.g. https://www.demo.com"
            />
          </FormModalField>
          <FormModalField label="Out-of-Scope Targets">
            <TagInput
              values={form.out_of_scope}
              onChange={(v) => setForm({ ...form, out_of_scope: v })}
              placeholder="Type a target and press Enter — e.g. admin.habilesec.com"
            />
            <p className="text-xs text-gray-400 mt-1">These targets will never be included in a scan.</p>
          </FormModalField>
          <FormModalField label="Authorization" required>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.authorized}
                onChange={(e) => setForm({ ...form, authorized: e.target.checked })}
                required
              />
              I confirm that I am authorized to perform security testing against these targets.
            </label>
          </FormModalField>
          <div className="grid grid-cols-2 gap-4">
            <FormModalField label="Testing Window — Start" required>
              <input
                type="datetime-local"
                className={formInputCls(false)}
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                required
              />
            </FormModalField>
            <FormModalField label="Testing Window — End" required>
              <input
                type="datetime-local"
                className={formInputCls(false)}
                value={form.end_date}
                min={form.start_date || undefined}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                required
              />
            </FormModalField>
          </div>
          <FormModalField label="Environment" required>
            <select
              className={formInputCls(false)}
              value={form.environment}
              onChange={(e) => setForm({ ...form, environment: e.target.value })}
              required
            >
              <option value="Production">Production</option>
              <option value="Staging">Staging</option>
              <option value="Development">Development</option>
            </select>
          </FormModalField>
        </FormModalBody>
        <FormModalFooter formId="vapt-project-form" onCancel={() => setShowModal(false)} saving={saving} submitLabel="Project" />
      </FormModal>

      {inventoryModalCustomer && (
        <VaptAssetInventoryModal
          customerId={inventoryModalCustomer.id}
          customerName={inventoryModalCustomer.name}
          onClose={() => setInventoryModalCustomer(null)}
          onNavigateToScan={async () => {
            const customerId = inventoryModalCustomer.id;
            setInventoryModalCustomer(null);
            const existing = assetProjects.find((p) => String(p.customer_id) === String(customerId));
            if (!existing) {
              // No persisted project yet — trigger the persisting sync first
              // so VaptCreateScan.js's project picker has a real Asset VAPT
              // project to select for this customer.
              try {
                await syncVaptProjectFromAssetManagerApi(customerId);
                load();
              } catch (err) {
                // Fall through — VaptCreateScan.js will show its own
                // "no projects" state if the sync genuinely found nothing.
              }
            }
            navigate("/ai-vapt/scans/new");
          }}
        />
      )}
    </div>
  );
};

export default VaptProjects;
